import { useDeferredValue, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail, fraudClient } from '@/lib/api/fraud-client'
import { useCreateChargeback, useFraudChargebacks, useFraudReloads, useUpdateChargeback } from '@/lib/api/fraud-queries'
import { CHARGEBACK_STATES, type ChargeLookup, type ChargebackState, type FraudBlockScope, type FraudReload } from '@/lib/api/fraud-types'
import { useAuth } from '@/lib/auth'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { Banner, Spinner } from './shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, moneyFormatter, userDisplayName } from './format'
import { cardText } from './card-brand'

const FIELD = 'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-sm'

const STATE_CHIP: Record<ChargebackState, string> = {
  received: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  won: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  accepted: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
}

function reloadLine(r: FraudReload): string {
  const card = r.card ? cardText(r.card.brand, r.card.last4) : r.instrument_id || 'card'
  return `${fmtDateTime(r.timestamp)} · ${card} · #${r.id} · ${r.provider_transfer_id || EMPTY}${r.refunded ? ' · refunded' : ''}`
}

/**
 * Pick the reload being disputed: search by attendee, card last four,
 * transaction id or Finix transfer id. Local data only, so it is instant.
 */
function ReloadPicker({ eventId, selected, onSelect, money }: {
  eventId: number
  selected: FraudReload | null
  onSelect: (reload: FraudReload | null) => void
  money: (v: number | string | null | undefined) => string
}) {
  const [q, setQ] = useState('')
  const deferred = useDeferredValue(q)
  const [open, setOpen] = useState(false)
  const { data, isFetching } = useFraudReloads(eventId, deferred.trim(), open)
  const rows = data ?? []

  if (selected) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-3 py-2 text-sm">
        <span className="font-medium text-neutral-900 dark:text-white">{userDisplayName(selected.user)}</span>
        <span>{money(selected.charged)}</span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{reloadLine(selected)}</span>
        {selected.chargeback_id && (
          <span className={cn(PILL, 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400')}>chargeback #{selected.chargeback_id} already recorded</span>
        )}
        <button type="button" onClick={() => onSelect(null)} className="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:underline">Change</button>
      </div>
    )
  }
  return (
    <div className="relative">
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder="Attendee name, email, phone, card last 4, transaction id or TR… id"
      />
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-lg text-sm">
          {rows.length === 0 ? (
            <li className="px-3 py-2 text-neutral-500 dark:text-neutral-400">{isFetching ? 'Searching…' : 'No card reloads match.'}</li>
          ) : rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(r); setOpen(false); setQ('') }}
                className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-neutral-900 dark:text-white">{userDisplayName(r.user)}</span>
                  <span>{money(r.charged)}</span>
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  {reloadLine(r)}{r.chargeback_id ? ` · chargeback #${r.chargeback_id}` : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ChargebackForm({ eventId, currency }: { eventId: number; currency?: string | null }) {
  const create = useCreateChargeback(eventId)
  const { token, user } = useAuth()
  const money = moneyFormatter(currency)
  const [selected, setSelected] = useState<FraudReload | null>(null)
  const [transferId, setTransferId] = useState('')
  const [disputeId, setDisputeId] = useState('')
  const [respondBy, setRespondBy] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [state, setState] = useState<ChargebackState>('received')
  const [notes, setNotes] = useState('')
  const [blockUser, setBlockUser] = useState(true)
  const [blockScope, setBlockScope] = useState<FraudBlockScope>('event')
  const [lookup, setLookup] = useState<ChargeLookup | null>(null)
  const [looking, setLooking] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const alreadyRecorded = lookup?.disputes.find((d) => d.id === disputeId)?.existing_chargeback
    ?? (selected?.chargeback_id && !disputeId ? { id: selected.chargeback_id } : null)

  const pick = (reload: FraudReload | null) => {
    setSelected(reload)
    setTransferId(reload?.provider_transfer_id ?? '')
    setLookup(null); setLookupError(null); setDisputeId(''); setRespondBy(null)
  }

  const lookUp = async () => {
    setLookupError(null)
    setLooking(true)
    try {
      const result = await fraudClient.lookupCharge(token!, eventId, selected ? { transaction: selected.id } : { transfer_id: transferId.trim() })
      setLookup(result)
      if (result.transfer) setTransferId(result.transfer.id)
      if (!selected && result.transaction) setSelected(result.transaction)
      // Prefill from the dispute Finix knows about (the first not yet recorded, else the first).
      const dispute = result.disputes.find((d) => !d.existing_chargeback) ?? result.disputes[0]
      if (dispute) {
        setDisputeId(dispute.id)
        setReason(dispute.reason)
        setState(dispute.mapped_state)
        setRespondBy(dispute.respond_by)
        if (dispute.amount) setAmount(dispute.amount)
      }
    } catch (err) {
      setLookupError(apiErrorDetail(err, 'Could not reach Finix.'))
    } finally {
      setLooking(false)
    }
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!selected && !transferId.trim()) {
      setError('Pick the reload, or enter its Finix transfer id.')
      return
    }
    try {
      const result = await create.mutateAsync({
        transaction: selected?.id ?? null,
        provider_transfer_id: transferId.trim() || undefined,
        provider_dispute_id: disputeId || undefined,
        respond_by: respondBy,
        amount: amount || null,
        reason, state, notes,
        block_user: blockUser,
        block_scope: blockUser ? blockScope : undefined,
      })
      setSuccess(`Chargeback #${result.id} recorded${result.block ? ` and the attendee is blocked (${result.block.scope}).` : '.'}`)
      pick(null); setAmount(''); setReason(''); setNotes(''); setState('received')
    } catch (err) {
      setError(apiErrorDetail(err, 'Could not record the chargeback.'))
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Banner kind="error">{error}</Banner>}
      {success && <Banner kind="success">{success}</Banner>}
      <label className="block text-sm">
        <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Disputed reload</span>
        <ReloadPicker eventId={eventId} selected={selected} onSelect={pick} money={money} />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Finix transfer id{selected ? '' : ' (if the reload is not listed)'}</span>
          <Input value={transferId} onChange={(e) => setTransferId(e.target.value)} placeholder="TR…" readOnly={!!selected} className={selected ? 'bg-neutral-50 dark:bg-neutral-900' : ''} />
        </label>
        <Button type="button" variant="outline" size="sm" onClick={lookUp} disabled={looking || (!selected && !transferId.trim())} className="mb-0.5">
          {looking ? 'Looking up…' : 'Look up charge'}
        </Button>
      </div>
      {lookupError && <Banner kind="error">{lookupError}</Banner>}
      {lookup && (
        <div className="rounded-md border border-neutral-200 dark:border-neutral-800 px-3 py-2 text-sm space-y-1">
          {lookup.transfer && (
            <p className="text-neutral-700 dark:text-neutral-300">
              Finix charge <span className="font-mono text-xs">{lookup.transfer.id}</span>: {lookup.transfer.amount ? `${lookup.transfer.currency} ${lookup.transfer.amount}` : EMPTY} · {lookup.transfer.state.toLowerCase()}
              {lookup.transfer.failure_code ? ` (${lookup.transfer.failure_code})` : ''} · {fmtDateTime(lookup.transfer.created_at)}
            </p>
          )}
          {lookup.disputes.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400">No dispute filed at Finix yet. Record what you know below; the sync updates it once the issuer files.</p>
          ) : lookup.disputes.map((d) => (
            <p key={d.id} className="text-neutral-700 dark:text-neutral-300">
              Dispute <span className="font-mono text-xs">{d.id}</span>: {d.reason || 'no reason code'} · {d.state.toLowerCase()}
              {d.amount ? ` · ${money(d.amount)}` : ''}{d.respond_by ? ` · respond by ${fmtDateTime(d.respond_by)}` : ''}
              {d.existing_chargeback
                ? <span className={cn(PILL, 'ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400')}>already recorded as #{d.existing_chargeback.id}</span>
                : <span className={cn(PILL, 'ml-2 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>filled in below</span>}
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Disputed amount (blank = the reload's charge)</span>
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder={selected ? String(selected.charged.toFixed(2)) : '0.00'} />
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">State</span>
          <select value={state} onChange={(e) => setState(e.target.value as ChargebackState)} className={FIELD}>
            {CHARGEBACK_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Reason code</span>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. FRAUD, PRODUCT_NOT_RECEIVED" />
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Notes</span>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-700 dark:text-neutral-300">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={blockUser} onChange={(e) => setBlockUser(e.target.checked)} />
          Block the attendee
        </label>
        {blockUser && (
          <select value={blockScope} onChange={(e) => setBlockScope(e.target.value as FraudBlockScope)} className="rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1 text-sm">
            <option value="event">at this event</option>
            <option value="organization">across the organization (org admins)</option>
            {user?.is_superuser && <option value="account">account-wide (EventPay staff)</option>}
          </select>
        )}
      </div>
      {alreadyRecorded ? (
        <p className="text-sm text-amber-700 dark:text-amber-400">This dispute is already recorded as chargeback #{alreadyRecorded.id}; update it in the list below instead.</p>
      ) : (
        <Button type="submit" size="sm" disabled={create.isPending}>{create.isPending ? 'Recording…' : 'Record chargeback'}</Button>
      )}
    </form>
  )
}

export function ChargebacksTab({ eventId, currency }: { eventId: number; currency?: string | null }) {
  const canManage = usePermission('can_manage_fraud')
  const [showForm, setShowForm] = useState(false)
  const { data, isLoading, error } = useFraudChargebacks(eventId)
  const update = useUpdateChargeback(eventId)
  const [rowError, setRowError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<number | null>(null)
  const money = moneyFormatter(currency)
  const rows = data ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Disputes on this event's card reloads. Finix disputes are imported automatically when the sync job runs; record one here to react before it lands.
        </p>
        {canManage && (
          <Button size="sm" variant={showForm ? 'outline' : 'default'} onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Close' : 'Record a chargeback'}
          </Button>
        )}
      </div>

      {showForm && canManage && (
        <Card>
          <CardHeader><CardTitle className="text-base">Record a chargeback</CardTitle></CardHeader>
          <CardContent><ChargebackForm eventId={eventId} currency={currency} /></CardContent>
        </Card>
      )}

      {rowError && <Banner kind="error">{rowError}</Banner>}

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load chargebacks: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">No chargebacks recorded for this event.</p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Attendee</th>
                <th className={TH}>Amount</th>
                <th className={TH}>State</th>
                <th className={TH}>Reason</th>
                <th className={TH}>Received</th>
                <th className={TH}>Respond by</th>
                <th className={TH}>Dispute id</th>
                <th className={TH}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((cb) => (
                <tr key={cb.id} className={ROW}>
                  <td className={TD}>{cb.user_name || (cb.user ? `User #${cb.user}` : 'Unmatched')}</td>
                  <td className={TD}>{money(cb.amount)}</td>
                  <td className={TD}>
                    {canManage ? (
                      <select
                        value={cb.state}
                        disabled={pendingId === cb.id}
                        onChange={async (e) => {
                          setRowError(null)
                          setPendingId(cb.id)
                          try {
                            await update.mutateAsync({ chargebackId: cb.id, data: { state: e.target.value as ChargebackState } })
                          } catch (err) {
                            setRowError(apiErrorDetail(err, 'Could not update the chargeback.'))
                          } finally {
                            setPendingId(null)
                          }
                        }}
                        className={cn(PILL, STATE_CHIP[cb.state], 'border-0 pr-6 cursor-pointer')}
                      >
                        {CHARGEBACK_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    ) : (
                      <span className={cn(PILL, STATE_CHIP[cb.state])}>{CHARGEBACK_STATES.find((s) => s.value === cb.state)?.label ?? cb.state}</span>
                    )}
                  </td>
                  <td className={TD}>{cb.reason || EMPTY}</td>
                  <td className={TD}>{fmtDateTime(cb.received_at)}</td>
                  <td className={TD}>{cb.respond_by ? fmtDateTime(cb.respond_by) : EMPTY}</td>
                  <td className={TD}>
                    <span className="font-mono text-xs">{cb.provider_dispute_id || EMPTY}</span>
                    {cb.provider_transfer_id && <div className="font-mono text-xs text-neutral-500">{cb.provider_transfer_id}</div>}
                  </td>
                  <td className={cn(TD, 'whitespace-normal max-w-[20rem]')}>{cb.notes || EMPTY}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
